/**
 * Turns the map on screen into a picture.
 *
 * There is no browser API for "screenshot this element", so the map is
 * recomposed onto a canvas. OL composites every layer — tile, vector,
 * heatmap, raster — onto its own `<canvas>` inside a `.ol-layer` element, so
 * there is only one kind of thing to read here: walk those canvases in DOM
 * order (already OL's paint order) and draw each one, straight across, onto
 * the export canvas.
 *
 * Each layer canvas is positioned purely through its own CSS `transform`
 * (OL's pan/zoom-animation offset) — pre-multiplying that matrix by `scale`
 * both upscales the pixels and moves them to the right place in the larger
 * export canvas in one step.
 *
 * WHY A LAYER CAN GO MISSING
 * Drawing an <img> that was loaded without CORS taints the canvas it's drawn
 * into. OL paints tiles onto its per-layer canvas as part of NORMAL
 * rendering, so a tile server without CORS headers taints that WHOLE layer's
 * canvas. The known basemaps are all served with CORS headers (see
 * MapViewOL's `crossOrigin: 'anonymous'` on the basemap source), but an
 * admin-configured WMS/ArcGIS service might not be — so each layer canvas is
 * checked in isolation (a cheap 1×1 `getImageData` read, not `drawImage`,
 * which does NOT throw on a tainted canvas source — it just silently
 * propagates the taint to whatever it's drawn onto) BEFORE it is composited
 * onto the shared export canvas. A tainted layer is skipped and counted
 * below rather than poisoning every other layer's export along with it.
 */

export interface MapCapture {
  canvas: HTMLCanvasElement;
  /** Layers the export could not read, usually a tile server without CORS headers. */
  missingTiles: number;
}

/** A sub-region of the container, in CSS pixels relative to its own
 *  top-left corner — e.g. the Print tool's on-map frame box. Omit to
 *  capture the whole container, the original behaviour. */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Composes the visible map into a canvas.
 *
 * @param container the `.ol-viewport` element
 * @param scale     pixel density; 2 gives a print-sharp image
 * @param cropRect  capture only this region of `container` instead of all of it
 */
export async function captureMapOL(
  container: HTMLElement,
  scale = 2,
  cropRect?: CropRect,
): Promise<MapCapture> {
  const bounds = container.getBoundingClientRect();
  const crop = cropRect ?? { x: 0, y: 0, width: bounds.width, height: bounds.height };
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(crop.width * scale);
  canvas.height = Math.round(crop.height * scale);

  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not prepare the image.');

  context.save();
  context.scale(scale, scale);
  // A ground to draw on: layers may not cover the full frame while panning.
  context.fillStyle = '#f1f5f9';
  context.fillRect(0, 0, crop.width, crop.height);
  context.restore();

  // Reused across layers purely to avoid allocating a fresh probe canvas per
  // layer; its own size is irrelevant since only a 1×1 read is ever taken.
  const probe = document.createElement('canvas');
  probe.width = 1;
  probe.height = 1;
  const probeContext = probe.getContext('2d');

  const isTainted = (layerCanvas: HTMLCanvasElement): boolean => {
    if (!probeContext) return false;
    probeContext.clearRect(0, 0, 1, 1);
    probeContext.drawImage(layerCanvas, 0, 0, 1, 1);
    try {
      probeContext.getImageData(0, 0, 1, 1);
      return false;
    } catch {
      return true;
    }
  };

  let missingLayers = 0;
  const layerCanvases = Array.from(
    container.querySelectorAll<HTMLCanvasElement>('.ol-layer canvas, canvas.ol-layer'),
  );

  for (const layerCanvas of layerCanvases) {
    if (layerCanvas.width === 0 || layerCanvas.height === 0) continue;

    if (isTainted(layerCanvas)) {
      // See this file's doc comment — drawing it anyway would taint the
      // WHOLE export canvas, not just skip this one layer.
      missingLayers += 1;
      continue;
    }

    const opacityText =
      (layerCanvas.parentElement as HTMLElement | null)?.style.opacity || layerCanvas.style.opacity;
    const opacity = opacityText === '' ? 1 : Number(opacityText);

    const match = layerCanvas.style.transform.match(/^matrix\(([^)]*)\)$/);
    const [a, b, c, d, tx, ty] = match ? match[1].split(',').map(Number) : [1, 0, 0, 1, 0, 0];

    context.setTransform(
      a * scale,
      b * scale,
      c * scale,
      d * scale,
      (tx - crop.x) * scale,
      (ty - crop.y) * scale,
    );
    context.globalAlpha = Number.isFinite(opacity) ? opacity : 1;
    context.drawImage(layerCanvas, 0, 0);
  }

  context.setTransform(1, 0, 0, 1, 0, 0);
  context.globalAlpha = 1;

  return { canvas, missingTiles: missingLayers };
}
